'use client'

import {
  FC,
  useMemo,
  useCallback,
  CSSProperties,
  useEffect,
  useRef,
} from 'react'
import { useRouter } from 'next/navigation'
import useI18n from 'Hooks/useI18n'
import { withLocale } from '@/locales'
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil'
import { userState } from '@/store/user'
import useCountdown from '@/hooks/useCountdown'
import { URL_SUBSCRIBE } from '@/constant/subscribe'
import { SUMMER_SALE_DEADLINE_TIME } from '@/constant/date'
import {
  homeFilterDrawerOpenState,
  homeSelectedTagIdsState,
  homeSortingState,
} from '@/store/home'
import { TagsSFW } from '@/constant/tags'
import { isMobileVersion } from '@/utils/dom'
import AnniversaryBannerCard from './AnniversaryBannerCard'
import HalloweenBannerCard from './HalloweenBannerCard'
// 引入Swiper相关组件和样式
import { Swiper, SwiperSlide } from 'swiper/react'
import { Pagination, Autoplay } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/pagination'
import NewTagBanner from './NewTagBanner'
import CreateReelContestBanner from './CreateReelContestBanner'
import { getRecentChattedCharacter } from '@/apis/user'
import { GemsAndSubscriptionPageSource } from '@/store/chat'
import BlackFriBannerCard from './BlackFriBannerCard'
import RelBannerCard from './RelBannerCard'
import { Activity, ActivityHomeBannerCardViewModel } from '@/types/activity'
import {
  getActivityButtonBackgroundStyle,
  hasActivityButtonOwnBackground,
  shouldApplyActivityButtonOverlay,
} from '@/components/activity/actionButtonStyle'
import InlineRichText from '@/components/activity/InlineRichText'
import styles from './BannerCardCarousel.module.css'
import { useQuickTracking } from '@/provider/QuickTrackingProvider'
import { getPlatform } from '@/utils/platform'
import {
  buildHomeActivityBannerCardViewModel,
  getActivityHomeBannerDisplayScope,
  hasHomeActivityV2Banner,
  isHomeActivityBannerVisibleForScope,
  parsePreferredActivityTimestamp,
  resolveLegacyActivityWebJumpUrl,
} from '@/utils/activity'
import { useMount } from 'ahooks'
import clsx from 'clsx'
import {
  evictHomeMediaFromPersistentCache,
  registerHomeMediaForPersistentCache,
} from '@/packages/sw-prefetch/src/prefetch/homeMediaCacheRegistration'

// 自动滚动间隔时间
const AUTO_SCROLL_INTERVAL = 3000

// 滑动阈值
const SWIPE_THRESHOLD = 15

// 轮播项类型定义
type BannerItem = {
  id: string
  title: string
  subtitle?: string
  component: React.ReactNode
  link: string
  mediaUrls?: string[]
}

const toCssUrl = (url: string) => `url(${url})`

const getLocalizedNewUserBannerBackgroundUrl = (
  language: string,
  type: 'subscription' | 'discount' | undefined
) => {
  if (type === 'subscription') {
    switch (language) {
      case 'de':
      case 'en':
      case 'es':
      case 'fr':
      case 'it':
      case 'ja':
      case 'ko':
      case 'pt':
      case 'ru':
      case 'zh-tw':
        return `/activity/new_user_subscription_${language}.png`
      case 'pt-br':
        return '/activity/new_user_subscription_pt.png'
      default:
        return '/activity/new_user_subscription_en.png'
    }
  }

  switch (language) {
    case 'de':
    case 'en':
    case 'es':
    case 'fr':
    case 'it':
    case 'ja':
    case 'ko':
    case 'pt':
    case 'ru':
    case 'zh-tw':
      return `/activity/new_user_banner_${language}.png`
    case 'pt-br':
      return '/activity/new_user_banner_pt-br.png'
    default:
      return '/activity/new_user_banner_en.png'
  }
}

const getNewTagBannerBackgroundUrl = () =>
  isMobileVersion()
    ? '/activity/newtagbanner-mobileweb.png'
    : '/activity/newtagbanner-pc.png'

// 宝石图片组件，替换原来的ExploreBannerGem
const ActivityGems: FC = () => {
  const { t } = useI18n()

  return (
    <div className="relative">
      <img
        src="/activity/activity_gems.png"
        alt={t('Gems')}
        className="h-17 w-auto object-contain"
      />
    </div>
  )
}

const getLegacyBannerBackgroundUrl = (activity: Activity): string => {
  const firstItem = activity.banner?.background_url_list?.[0]

  if (typeof firstItem?.url === 'string' && firstItem.url.trim()) {
    return firstItem.url
  }

  if (Array.isArray(firstItem?.urls)) {
    return (
      firstItem.urls.find(
        (url): url is string => typeof url === 'string' && Boolean(url.trim())
      ) || ''
    )
  }

  return ''
}

// 动态 Banner 组件
const LegacyDynamicBannerCard: FC<{
  backgroundUrl: string
  infoStartTime: string | number | undefined
  infoEndTime: string | number | undefined
  legacyInfoStartTime?: string
  legacyInfoEndTime?: string
}> = ({
  backgroundUrl,
  infoStartTime,
  infoEndTime,
  legacyInfoStartTime,
  legacyInfoEndTime,
}) => {
  const startTime =
    parsePreferredActivityTimestamp(infoStartTime, legacyInfoStartTime) || 0
  const endTime =
    parsePreferredActivityTimestamp(infoEndTime, legacyInfoEndTime) || 0
  const { isExpired, isStarted, dd, hh, mm, ss } = useCountdown(
    endTime,
    startTime
  )

  const showCountdown = isStarted && !isExpired

  return (
    <div
      className={`${styles['dynamic-banner-card']} relative h-full w-full bg-cover bg-center`}
      style={{
        backgroundImage: `url(${backgroundUrl})`,
      }}
    >
      {showCountdown && (
        <div
          className={`${styles['dynamic-banner-countdown']} flex items-center justify-center font-mono`}
        >
          <span
            className={`${styles['banner-countdown-item']} flex items-center justify-center rounded-sm bg-black/20 text-center text-white`}
          >
            {dd}d
          </span>
          <span
            className={`${styles['banner-countdown-separator']} text-white/80`}
          >
            :
          </span>
          <span
            className={`${styles['banner-countdown-item']} flex items-center justify-center rounded-sm bg-black/20 text-center text-white`}
          >
            {hh}h
          </span>
          <span
            className={`${styles['banner-countdown-separator']} text-white/80`}
          >
            :
          </span>
          <span
            className={`${styles['banner-countdown-item']} flex items-center justify-center rounded-sm bg-black/20 text-center text-white`}
          >
            {mm}m
          </span>
          <span
            className={`${styles['banner-countdown-separator']} text-white/80`}
          >
            :
          </span>
          <span
            className={`${styles['banner-countdown-item']} flex items-center justify-center rounded-sm bg-black/20 text-center text-white`}
          >
            {ss}s
          </span>
        </div>
      )}
    </div>
  )
}

const V2DynamicBannerCard: FC<{
  banner: ActivityHomeBannerCardViewModel
}> = ({ banner }) => {
  const { t } = useI18n()
  const startTime = banner.event_start_time || 0
  const endTime = banner.event_end_time || 0
  const { isExpired, isStarted, dd, hh, mm, ss } = useCountdown(
    endTime,
    startTime
  )

  const showCountdown = !!endTime && isStarted && !isExpired
  const title = banner.title || t('Activity')
  const subtitle = banner.subtitle
  const ctaLabel = banner.cta_label || t('View details')
  const hasHeaderAsset = Boolean(banner.header_image_url)
  const hasSubtitle = Boolean(subtitle?.trim())
  const ctaStyle = banner.cta_style
  const hasCustomCtaBackground = hasActivityButtonOwnBackground(ctaStyle)
  const hasCtaOverlay = ctaStyle
    ? shouldApplyActivityButtonOverlay(ctaStyle)
    : false
  const resolvedCtaStyle = ctaStyle
    ? getActivityButtonBackgroundStyle(ctaStyle)
    : undefined

  return (
    <div
      className={`${styles['dynamic-banner-card']} ${styles['dynamic-banner-v2-card']} relative h-full w-full overflow-hidden bg-cover bg-center`}
      style={{
        backgroundImage: banner.background_image_url
          ? `url(${banner.background_image_url})`
          : 'linear-gradient(135deg, #0f172a 0%, #1e293b 52%, #0f766e 100%)',
      }}
    >
      <div className={styles['dynamic-banner-v2-frame']}>
        <div className={styles['dynamic-banner-v2-top-spacer']} />

        <div className={styles['dynamic-banner-v2-hero']}>
          {hasHeaderAsset ? (
            <img
              src={banner.header_image_url}
              alt={title}
              className={styles['dynamic-banner-v2-header']}
            />
          ) : (
            <p className={styles['dynamic-banner-v2-hero-fallback-title']}>
              <InlineRichText text={title} baseFontWeight={600} />
            </p>
          )}
        </div>

        <div className={styles['dynamic-banner-v2-hero-copy-spacer']} />

        <div className={styles['dynamic-banner-v2-copy']}>
          <p
            className={clsx(styles['dynamic-banner-v2-primary-text'], {
              [styles['dynamic-banner-v2-primary-text-expanded']]: !hasSubtitle,
            })}
          >
            <InlineRichText
              text={hasHeaderAsset ? title : ''}
              fallback={'\u00A0'}
              baseFontWeight={600}
            />
          </p>

          {hasSubtitle ? (
            <p className={styles['dynamic-banner-v2-secondary-text']}>
              <InlineRichText text={subtitle || ''} />
            </p>
          ) : null}
        </div>

        <div className={styles['dynamic-banner-v2-copy-countdown-spacer']} />

        <div className={styles['dynamic-banner-v2-countdown-row']}>
          {showCountdown ? (
            <>
              <span className={styles['dynamic-banner-v2-countdown-chip']}>
                {dd}d
              </span>
              <span className={styles['dynamic-banner-v2-countdown-separator']}>
                :
              </span>
              <span className={styles['dynamic-banner-v2-countdown-chip']}>
                {hh}h
              </span>
              <span className={styles['dynamic-banner-v2-countdown-separator']}>
                :
              </span>
              <span className={styles['dynamic-banner-v2-countdown-chip']}>
                {mm}m
              </span>
              <span className={styles['dynamic-banner-v2-countdown-separator']}>
                :
              </span>
              <span className={styles['dynamic-banner-v2-countdown-chip']}>
                {ss}s
              </span>
            </>
          ) : null}
        </div>

        <div className={styles['dynamic-banner-v2-countdown-cta-spacer']} />

        <div className={styles['dynamic-banner-v2-cta-row']}>
          <div
            aria-hidden="true"
            className={clsx(styles['dynamic-banner-v2-cta'], {
              [styles['dynamic-banner-v2-cta-custom']]: hasCustomCtaBackground,
            })}
            style={resolvedCtaStyle}
          >
            {ctaStyle?.background_image_url && !hasCtaOverlay ? (
              <span
                className={styles['dynamic-banner-v2-cta-image-mask']}
                aria-hidden
              />
            ) : null}
            <InlineRichText
              text={ctaLabel}
              className={styles['dynamic-banner-v2-cta-label']}
              baseFontWeight={600}
            />
          </div>
        </div>

        <div className={styles['dynamic-banner-v2-bottom-spacer']} />
      </div>
    </div>
  )
}

const BannerCardComponent: FC<{
  title: string
  subtitle?: string
  deadline?: number // 可选，如果不提供则不显示倒计时
  isNewUser?: boolean // 决定使用哪种背景色
  activityLabel?: string // 可选，Activity标签的文本，默认为'Activity'
  forNewUser?: boolean // 是否为新用户专用活动(应用新背景)
  type?: 'subscription' | 'discount' //(应用不同新背景)
}> = ({
  title,
  subtitle,
  deadline,
  isNewUser = false,
  activityLabel,
  forNewUser = false,
  type,
}) => {
  const { t, lang } = useI18n()
  // 修复：无条件调用useCountdown，使用0作为默认值
  const { isExpired, dd, hh, mm, ss } = useCountdown(deadline || 0)
  const platform = getPlatform()
  const countdownPositionStyle = useMemo(
    () =>
      ({
        '--new-user-countdown-bottom': platform === 'web_pc' ? '90px' : '105px',
        '--new-user-countdown-bottom-cqw':
          platform === 'web_pc' ? '35cqw' : '35cqw',
      }) as CSSProperties,
    [platform]
  )

  // 根据deadline是否存在决定是否显示倒计时
  const shouldShowCountdown = deadline !== undefined && !isExpired
  // 使用提供的activityLabel或默认值
  const labelText = activityLabel || t('Activity')
  if (!forNewUser) {
    return (
      <div
        className="relative flex h-full w-full flex-col items-center justify-start bg-cover bg-center p-4"
        style={{
          backgroundImage: isNewUser
            ? 'url(/activity/bg_new_user.png)'
            : 'url(/activity/bg_first_recharge.png)',
        }}
      >
        {/* 顶部标题 - 使用绝对定位放置在左上角 */}
        <div className="absolute left-0 top-[-2px]">
          <span className="inline-block rounded-br-lg bg-[rgba(0,0,0,0.2)] px-3 py-1 text-xs text-white/50">
            {labelText}
          </span>
        </div>

        {/* 宝石图标 */}
        <div className="mt-8 flex items-center justify-center">
          <ActivityGems />
        </div>

        {/* 文字内容 */}
        <div className="mt-2 flex h-16 flex-col items-center justify-between text-center">
          <section>
            <p className="text-xs font-medium text-white/80">{title}</p>

            {/* 副标题或倒计时*/}
            {subtitle && (
              <p className="!text-xs font-medium text-white/80">{subtitle}</p>
            )}
          </section>

          {/* 倒计时显示 - 根据shouldShowCountdown决定是否显示 */}
          {shouldShowCountdown && (
            <div className="banner-countdown-container mt-1 flex items-center justify-center font-mono">
              <span className="banner-countdown-item flex items-center justify-center rounded-sm bg-black/20 text-center">
                {dd}d
              </span>
              <span className="text-white/80">:</span>
              <span className="banner-countdown-item flex items-center justify-center rounded-sm bg-black/20 text-center">
                {hh}h
              </span>
              <span className="text-white/80">:</span>
              <span className="banner-countdown-item flex items-center justify-center rounded-sm bg-black/20 text-center">
                {mm}m
              </span>
              <span className="text-white/80">:</span>
              <span className="banner-countdown-item flex items-center justify-center rounded-sm bg-black/20 text-center">
                {ss}s
              </span>
            </div>
          )}
        </div>

        {/* 查看按钮 */}
        <div className="mt-4 w-full">
          <button className="h-[26px] w-full rounded-full bg-white/20 text-[13px] font-medium leading-[26px] text-white hover:bg-white/30">
            {t('View')}
          </button>
        </div>
      </div>
    )
  } else {
    return (
      <div
        className="relative flex h-full w-full flex-col items-center justify-start bg-cover bg-center p-4"
        style={{
          backgroundImage: toCssUrl(
            getLocalizedNewUserBannerBackgroundUrl(lang, type)
          ),
        }}
      >
        {/* 文字内容 */}
        {/* 倒计时显示 - 根据shouldShowCountdown决定是否显示 */}
        {shouldShowCountdown && (
          <div
            className={clsx(
              'banner-countdown-container mt-1 flex items-center justify-center font-mono',
              styles['new-user-countdown']
            )}
            style={countdownPositionStyle}
          >
            <span className="banner-countdown-item flex min-w-7 items-center justify-center rounded-md bg-black/20 px-[2px] text-center">
              {dd}d
            </span>
            <span className="text-white/80">:</span>
            <span className="banner-countdown-item flex min-w-7 items-center justify-center rounded-md bg-black/20 px-[2px] text-center">
              {hh}h
            </span>
            <span className="text-white/80">:</span>
            <span className="banner-countdown-item flex min-w-7 items-center justify-center rounded-md bg-black/20 px-[2px] text-center">
              {mm}m
            </span>
            <span className="text-white/80">:</span>
            <span className="banner-countdown-item flex min-w-7 items-center justify-center rounded-md bg-black/20 px-[2px] text-center">
              {ss}s
            </span>
          </div>
        )}

        {/* 查看按钮 */}
        {/* <div className="mt-4 w-full">
          <button></button>
        </div> */}
      </div>
    )
  }
}

interface BannerCardCarouselProps {
  showFirstRecharge: boolean
  showNewUser: boolean
  showSummerSale: boolean
  showAnniversaryBanner: boolean
  showBlackFri: boolean
  showHalloweenBanner: boolean
  showNewTagsBanner?: boolean
  showCreateReelsBanner?: boolean
  className?: string
  onMediaReady?: () => void
  // 动态配置的活动列表
  dynamicActivities?: Activity[]
  fromScreen?: string
}

// 主组件
const BannerCardCarousel: FC<BannerCardCarouselProps> = ({
  showFirstRecharge,
  showNewUser,
  showSummerSale,
  showBlackFri,
  showAnniversaryBanner,
  showHalloweenBanner,
  showNewTagsBanner,
  showCreateReelsBanner,
  className,
  onMediaReady,
  dynamicActivities = [],
  fromScreen,
}) => {
  const { trackEvent } = useQuickTracking()
  const platform = getPlatform()
  const { t, lang } = useI18n()
  const router = useRouter()
  const user = useRecoilValue(userState)
  const setSelectedTagIds = useSetRecoilState(homeSelectedTagIdsState)
  const openFilterDrawer = useSetRecoilState(homeFilterDrawerOpenState)
  const setSorting = useSetRecoilState(homeSortingState)
  const [, setPageSource] = useRecoilState(GemsAndSubscriptionPageSource)

  const handleHalloweenFilter = useCallback(() => {
    if (!TagsSFW.length) return
    const targetTag = 'Halloween 2025'
    const tagItem = TagsSFW.find(({ tag }) => tag === targetTag)
    const tagId = tagItem?.id ?? null
    if (!tagId) return
    setSorting('New Releases')
    // 选中第一个标签
    setSelectedTagIds([tagId])
    if (isMobileVersion()) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [setSorting, setSelectedTagIds])

  const handleNewtagsFilter = useCallback(() => {
    if (!TagsSFW.length) return
    const tagNames = ['Celebrity', 'Enemy to Lovers', 'Lore']
    const selectedIds = TagsSFW.filter(({ tag }) => tagNames.includes(tag)).map(
      ({ id }) => id
    )
    if (!selectedIds.length) return
    setSorting('New Releases')

    // 选中第一个标签
    setSelectedTagIds(selectedIds)
    if (isMobileVersion()) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
      openFilterDrawer(true)
    }
  }, [setSorting, openFilterDrawer, setSelectedTagIds])

  const handleCreateReels = useCallback(async () => {
    if (!user || !user.user_id) {
      return
    }
    getRecentChattedCharacter(user?.user_id)
      .then((characterId) => {
        if (characterId) {
          router.push(withLocale(`/chat/${characterId}`, lang))
        } else {
          router.push(withLocale('/', lang))
        }
      })
      .catch(() => {
        router.push(withLocale('/', lang))
      })
  }, [user, router, lang])

  // 处理动态配置的 banner
  const getDynamicBanners = useCallback(() => {
    const now = new Date().getTime()
    const scope = getActivityHomeBannerDisplayScope(fromScreen)

    return dynamicActivities
      .filter((activity) => {
        return isHomeActivityBannerVisibleForScope(
          activity,
          user?.user_id,
          scope,
          now
        )
      })
      .map((activity) => {
        const isV2Banner = hasHomeActivityV2Banner(activity)
        const bannerCard = isV2Banner
          ? buildHomeActivityBannerCardViewModel(activity, lang)
          : null
        const jumpUrl = isV2Banner
          ? bannerCard?.jump_url || activity.banner.web_jump_url
          : resolveLegacyActivityWebJumpUrl(activity) ||
            activity.banner.web_jump_url

        return {
          id: `dynamic_${activity.activity_id}`,
          title:
            (isV2Banner ? bannerCard?.title : undefined) ||
            activity.banner.description,
          mediaUrls: (isV2Banner
            ? [
                bannerCard?.background_image_url,
                bannerCard?.header_image_url,
                bannerCard?.cta_style?.background_image_url,
              ]
            : [getLegacyBannerBackgroundUrl(activity)]
          ).filter(
            (url): url is string => typeof url === 'string' && Boolean(url)
          ),
          component: isV2Banner ? (
            <V2DynamicBannerCard
              banner={bannerCard as ActivityHomeBannerCardViewModel}
            />
          ) : (
            <LegacyDynamicBannerCard
              backgroundUrl={getLegacyBannerBackgroundUrl(activity)}
              infoStartTime={activity.info.start_time_ts}
              infoEndTime={activity.info.end_time_ts}
              legacyInfoStartTime={activity.info.start_time}
              legacyInfoEndTime={activity.info.end_time}
            />
          ),
          link: jumpUrl,
        }
      })
  }, [dynamicActivities, fromScreen, lang, user?.user_id])

  // 轮播项列表
  const banners: BannerItem[] = useMemo(() => {
    // 获取动态配置的 banner
    const dynamicBanners = getDynamicBanners()

    // 根据条件判断显示轮播项
    return [
      // 先是写死的 banner
      showSummerSale
        ? {
            id: 'annual_plan',
            title: 'Summer Sale!',
            subtitle: '',
            mediaUrls: [
              '/activity/bg_first_recharge.png',
              '/activity/activity_gems.png',
            ],
            component: (
              <BannerCardComponent
                title={t('Summer Sale!')}
                deadline={SUMMER_SALE_DEADLINE_TIME}
              />
            ),
            link: URL_SUBSCRIBE,
          }
        : null,
      showNewUser
        ? {
            id: 'new_user',
            title: 'Exclusive savings for first-timers!',
            subtitle: '',
            mediaUrls: [
              getLocalizedNewUserBannerBackgroundUrl(lang, 'subscription'),
            ],
            component: (
              <BannerCardComponent
                title={t('Exclusive savings for first-timers!')}
                deadline={
                  user?.user_id
                    ? Number(user?.user_id) / 1000000 + 3 * 86400000
                    : 0
                }
                isNewUser={true} // 标记为新用户活动
                activityLabel={t('Deal')}
                forNewUser={true}
                type={'subscription'}
              />
            ),
            link: URL_SUBSCRIBE,
          }
        : null,
      showFirstRecharge
        ? {
            id: 'first_recharge',
            title: 'First Recharge Bonus',
            subtitle: 'Huge bonus, one-time only!',
            mediaUrls: [
              getLocalizedNewUserBannerBackgroundUrl(lang, 'discount'),
            ],
            component: (
              <BannerCardComponent
                title={t('First Recharge Bonus')}
                subtitle={t('Huge bonus, one-time only!')}
                // 不传deadline，所以不会显示倒计时
                activityLabel={t('Bonus')}
                forNewUser={true}
                type={'discount'}
              />
            ),
            link: '/balance',
          }
        : null,
      showBlackFri
        ? {
            id: 'blackFri',
            title: '',
            subtitle: '',
            mediaUrls: ['/activity/black_fri.png'],
            component: <BlackFriBannerCard />,
            link: URL_SUBSCRIBE,
          }
        : null,
      showBlackFri
        ? {
            id: 'relationship',
            title: '',
            subtitle: '',
            mediaUrls: ['/activity/rel_banner.png'],
            component: <RelBannerCard />,
            link: 'relationship',
          }
        : null,
      showAnniversaryBanner
        ? {
            id: 'anniversary',
            title: t('1st Anniversary Sale'),
            subtitle: '',
            mediaUrls: ['/activity/bg_anniversary.png'],
            component: <AnniversaryBannerCard />,
            link: URL_SUBSCRIBE,
          }
        : null,
      showHalloweenBanner
        ? {
            id: 'halloween_2025',
            title: t('Halloween 2025'),
            subtitle: '',
            mediaUrls: ['/activity/bg_halloween.png'],
            component: <HalloweenBannerCard />,
            link: 'halloween-filter', // 使用特殊标识来触发筛选逻辑
          }
        : null,
      showNewTagsBanner
        ? {
            id: 'newtags',
            title: t('New Tags'),
            subtitle: '',
            mediaUrls: [getNewTagBannerBackgroundUrl()],
            component: <NewTagBanner />,
            link: 'newtags',
          }
        : null,
      showCreateReelsBanner
        ? {
            id: 'createreels',
            title: t('Create Reels Contest'),
            subtitle: '',
            mediaUrls: ['/activity/reelcontestbanner-webmobile.png'],
            component: <CreateReelContestBanner />,
            link: 'createreels',
          }
        : null,
      // 最后插入动态配置的 banner
      ...dynamicBanners,
    ].filter((item) => item !== null)
  }, [
    getDynamicBanners,
    t,
    showSummerSale,
    showNewUser,
    user?.user_id,
    showFirstRecharge,
    showBlackFri,
    showAnniversaryBanner,
    showHalloweenBanner,
    showNewTagsBanner,
    showCreateReelsBanner,
    lang,
  ])

  const firstBannerMediaUrls = useMemo(
    () => banners[0]?.mediaUrls ?? [],
    [banners]
  )
  const firstBannerMediaKey = useMemo(
    () => `${banners[0]?.id || 'none'}:${firstBannerMediaUrls.join('|')}`,
    [banners, firstBannerMediaUrls]
  )
  const mediaReadyRef = useRef({
    key: '',
    ready: false,
  })
  const onMediaReadyRef = useRef(onMediaReady)

  useEffect(() => {
    onMediaReadyRef.current = onMediaReady
  }, [onMediaReady])

  useEffect(() => {
    if (mediaReadyRef.current.key !== firstBannerMediaKey) {
      mediaReadyRef.current = {
        key: firstBannerMediaKey,
        ready: false,
      }
    }
    if (mediaReadyRef.current.ready) return

    const markReady = () => {
      if (mediaReadyRef.current.ready) return
      mediaReadyRef.current.ready = true
      onMediaReadyRef.current?.()
    }

    if (!banners.length || firstBannerMediaUrls.length === 0) {
      markReady()
      return
    }

    let cancelled = false
    void registerHomeMediaForPersistentCache({
      images: firstBannerMediaUrls,
      videos: [],
    }).then(() => {
      if (cancelled) return
      let remaining = firstBannerMediaUrls.length

      firstBannerMediaUrls.forEach((url) => {
        const image = new window.Image()
        let settled = false
        const handleDone = () => {
          if (cancelled || settled) return
          settled = true
          remaining -= 1
          if (remaining <= 0) markReady()
        }

        image.onload = handleDone
        image.onerror = () => {
          evictHomeMediaFromPersistentCache(url)
          handleDone()
        }
        image.src = url
        if (image.complete) handleDone()
      })
    })

    return () => {
      cancelled = true
    }
  }, [banners.length, firstBannerMediaKey, firstBannerMediaUrls])

  useMount(() => {
    if (!banners.length) return
    trackEvent(
      'character_page_exposure',
      {
        scene: fromScreen,
        type: 'banner',
        banner: banners.map((item) => item?.id || ''),
        platform,
      },
      'EXP'
    )
  })

  // 无轮播项时不显示
  if (banners.length === 0) {
    return null
  }

  return (
    <div
      className={`relative w-full overflow-hidden rounded-lg bg-secondary-bg pt-[167.57%] ${className || ''}`}
    >
      <div className="absolute inset-0 z-0">
        {/* 使用Swiper组件实现轮播 */}
        <Swiper
          modules={[Pagination, Autoplay]}
          spaceBetween={0}
          slidesPerView={1}
          threshold={SWIPE_THRESHOLD} // 使用与原来相同的滑动阈值
          speed={300} // 保持与原来相同的过渡速度
          pagination={{
            clickable: true,
            // 不指定el，让Swiper自动创建分页器
            bulletClass: 'swiper-pagination-bullet custom-bullet',
            bulletActiveClass:
              'swiper-pagination-bullet-active custom-bullet-active',
          }}
          autoplay={{
            delay: AUTO_SCROLL_INTERVAL, // 使用与原来相同的自动轮播间隔
            disableOnInteraction: false, // 用户交互后继续自动轮播
          }}
          className={`${styles['banner-swiper']} h-full w-full`}
        >
          {banners.map((item) => {
            // 判断是否为动态 banner(支持站外跳转)
            const isDynamicBanner = item.id.startsWith('dynamic_')
            // 判断是否为外部链接
            const isExternalLink =
              item.link.startsWith('http://') ||
              item.link.startsWith('https://')

            // 动态 banner 使用 <a> 标签,支持站内外跳转
            if (isDynamicBanner) {
              return (
                <SwiperSlide key={item.id}>
                  <a
                    href={
                      isExternalLink ? item.link : withLocale(item.link, lang)
                    }
                    className="relative block h-full w-full"
                    aria-label={item.title}
                    target={isExternalLink ? '_blank' : undefined}
                    rel={isExternalLink ? 'noopener noreferrer' : undefined}
                    onClick={() => {
                      trackEvent(
                        'character_page_click',
                        {
                          scene: fromScreen,
                          type: 'banner',
                          banner: item.id,
                          platform,
                        },
                        'CLK'
                      )
                    }}
                  >
                    {item.component}
                  </a>
                </SwiperSlide>
              )
            }

            // 其他 banner 使用原有的 button 逻辑
            return (
              <SwiperSlide key={item.id}>
                <button
                  className="relative h-full w-full border-none bg-transparent p-0 text-left"
                  onClick={() => {
                    trackEvent(
                      'character_page_click',
                      {
                        scene: fromScreen,
                        type: 'banner',
                        banner: item.id,
                        platform,
                      },
                      'CLK'
                    )
                    // 检测是否是万圣节banner
                    if (item.link === 'halloween-filter') {
                      handleHalloweenFilter()
                      return
                    }
                    if (item.link === 'newtags') {
                      handleNewtagsFilter()
                      return
                    }
                    if (item.link === 'createreels') {
                      handleCreateReels()
                      return
                    }
                    if (item.link === '/balance') {
                      sessionStorage.setItem('fromHomeBanner', 'true')
                      setPageSource('banner')
                    }
                    if (item.link === 'relationship') {
                      handleCreateReels()
                      return
                    }

                    router.push(withLocale(item.link, lang))
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      // 检测是否是万圣节banner
                      if (item.link === 'halloween-filter') {
                        handleHalloweenFilter()
                        return
                      }
                      if (item.link === 'newtags') {
                        handleNewtagsFilter()
                        return
                      }
                      if (item.link === 'createreels') {
                        handleCreateReels()
                        return
                      }
                      if (item.link === '/balance') {
                        sessionStorage.setItem('fromHomeBanner', 'true')
                      }
                      if (item.link === 'relationship') {
                        handleCreateReels()
                        return
                      }
                      router.push(withLocale(item.link, lang))
                    }
                  }}
                  aria-label={item.title}
                >
                  {item.component}
                </button>
              </SwiperSlide>
            )
          })}
        </Swiper>
      </div>
    </div>
  )
}

export default BannerCardCarousel
